import type { Detection, LanguageRegistry } from "@codeshare/languages";
import { detectLanguage } from "@codeshare/languages";

export interface DocumentLanguageOptions {
  registry: LanguageRegistry;
  /** Content-based guess (the ML detector). Undefined = not confident. */
  detectContent: (content: string) => Promise<Detection | undefined>;
  /** Called whenever the resolved language changes. */
  onChange: (language: Detection) => void;
  debounceMs?: number;
}

/**
 * Decides a document's language: a manual override wins; otherwise filename/extension/shebang/
 * modeline detection, then (for unknown files) a debounced content guess.
 */
export class DocumentLanguage {
  readonly #options: Required<DocumentLanguageOptions>;
  #path: string | undefined;
  #content = "";
  #current: Detection;
  #timer: ReturnType<typeof setTimeout> | undefined;
  #generation = 0;

  constructor(options: DocumentLanguageOptions, path?: string, content = "") {
    this.#options = { debounceMs: 750, ...options };
    this.#path = path;
    this.#content = content;
    this.#current = detectLanguage({ path, content }, options.registry);
    if (this.#current.source === "default") this.#schedule();
  }

  get current(): Detection {
    return this.#current;
  }

  /** A new or renamed document: re-detect immediately (unless overridden). */
  setPath(path: string | undefined, content = this.#content): void {
    this.#path = path;
    this.#content = content;
    if (this.#current.source === "user") return;
    this.#generation++;
    this.#redetect(true);
  }

  /** The text changed: re-detect after a pause in typing (unless overridden). */
  contentChanged(content: string): void {
    this.#content = content;
    if (this.#current.source === "user") return;
    this.#schedule();
  }

  /** Manual choice from the language picker; `null` returns to automatic detection. */
  override(languageId: string | null): void {
    this.#generation++;
    clearTimeout(this.#timer);
    if (languageId === null) {
      this.#current = { id: "plaintext", source: "default", confidence: 0 };
      this.#redetect(true);
      return;
    }
    this.#set({ id: languageId, source: "user", confidence: 1 });
  }

  dispose(): void {
    clearTimeout(this.#timer);
    this.#generation++;
  }

  #schedule(): void {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#redetect(false), this.#options.debounceMs);
  }

  #redetect(immediate: boolean): void {
    const { registry, detectContent } = this.#options;
    const detection = detectLanguage({ path: this.#path, content: this.#content }, registry);
    if (detection.source !== "default") {
      this.#set(detection);
      return;
    }
    // Unknown file. Keep an earlier content guess until a new confident one arrives.
    if (this.#current.source !== "content") this.#set(detection);
    if (immediate) {
      this.#schedule();
      return;
    }
    const generation = ++this.#generation;
    const content = this.#content;
    void detectContent(content).then((guess) => {
      // Ignore results for text, paths, or overrides that have since changed.
      if (generation !== this.#generation || !guess) return;
      this.#set(guess);
    });
  }

  #set(detection: Detection): void {
    const changed = detection.id !== this.#current.id || detection.source !== this.#current.source;
    this.#current = detection;
    if (changed) this.#options.onChange(detection);
  }
}
